import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useQuery, useLazyQuery, useMutation } from '@apollo/react-hooks';
import {
  GET_CURRENT_ORGANIZATION,
  GET_ACTIVE_COUPONS,
  REDEEM_SELECTED_COUPONS,
} from '../../graphql/queries';

import Button from '../Button';
import colors from '../../styles/colors';
import { PhoneNumberInput } from '../Input';
import { getErrorMessage } from '../../util/ErrorUtil';
import * as Price from '../../util/Price';
import Error from '../Error';
import Container from '../../shared/Container';
import Section from '../../shared/Section';
import CouponList from './CouponsList';
import BaseModal, { ModalList } from '../modals/BaseModal';

const Form = styled.form`
  display: flex;
  flex-direction: column;
  width: 75%;

  @media (max-width: 991px) {
    width: 100%;
  }
`;

const HeaderText = styled.div`
  font-weight: bold;
  font-size: 2.4rem;
  color: ${colors.grey1};
  margin-top: 10px;
`;

const HelpText = styled.div`
  margin: 10px 0px 20px 0px;
  font-weight: 500px;
  font-size: 1.4rem;
  color: ${colors.grey2};
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-around;
  width: 100%;
  flex-direction: row;
  margin: auto;
  @media screen and (max-width: 450px) {
    padding-right: 40px;
  }
`;

const ValidCouponContainer = styled.div`
  padding: 50px;
  text-align: center;
`;

const ListHead = styled.div`
  display: flex;
  justify-content: center;
  padding: 20px;
  font-size: 1.8rem;
`;

const PageState = Object.freeze({
  EnterPhone: 'EnterPhone',
  ValidCoupon: 'ValidCoupon',
  InvalidCoupon: 'InvalidCoupon',
  CouponRedeemed: 'CouponRedeemed',
});

export default function RedeemCoupon() {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [activeCoupons, setActiveCoupons] = useState([]);
  const [errorMsg, setErrorMsg] = useState('');
  const [pageState, setPageState] = useState(PageState.EnterPhone);
  const [showRedeemConfirmModal, setShowRedeemConfirmModal] = useState(false);
  const [selectedCoupons, setSelectedCoupons] = useState([]);
  const [balance, setBalance] = useState(0);
  const { error } = useQuery(GET_CURRENT_ORGANIZATION);
  const [getActiveCoupons, { loading: getCouponsLoading }] = useLazyQuery(
    GET_ACTIVE_COUPONS,
    {
      fetchPolicy: 'network-only',
      onError(error) {
        setErrorMsg(getErrorMessage(error));
      },
      onCompleted(data) {
        const activeCoupons = data.getActiveCoupons;
        // const userId = data.getActiveCoupons.userId;
        if (activeCoupons && activeCoupons.length > 0) {
          setActiveCoupons(activeCoupons);
          setPageState(PageState.ValidCoupon);
        } else {
          setPageState(PageState.InvalidCoupon);
        }
      },
    },
  );
  const [redeemCoupon, { loading: redeemLoading }] = useMutation(REDEEM_SELECTED_COUPONS, {
    variables: {
      phoneNumber,
      coupons: selectedCoupons.map((coupon) => coupon._id),
    },
    onError(error) {
      setErrorMsg(getErrorMessage(error));
    },
    onCompleted(data) {
      console.log(data);
      setBalance(data.redeemCoupons);
      setPageState(PageState.CouponRedeemed);
    },
  });

  if (error) {
    setErrorMsg(getErrorMessage(error));
  }

  useEffect(() => {
    if (selectedCoupons.length > 0) {
      setErrorMsg('');
    }
  }, [selectedCoupons]);

  const redeemButtonClick = () => {
    if (selectedCoupons.length < 1) {
      setErrorMsg('Please select at least one or more coupons to be redeemed');
    } else {
      setErrorMsg('');
      setShowRedeemConfirmModal(true);
    }
  };

  return (
    <>
      <Container>
        <Section headerTitle="Redeem Discounts">
          <BaseModal
            open={showRedeemConfirmModal}
            modalType={ModalList.RedeemConfirmModal}
            onClose={setShowRedeemConfirmModal}
            onConfirm={() => {
              redeemCoupon();
              setShowRedeemConfirmModal(false);
            }}
            noDelete
            header={`Are you sure you want to redeem discount of $${Price.output(
              selectedCoupons.reduce(
                (total, item) => total + parseInt(item.amount),
                0,
              ),
              true,
            )}?`}
          />
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              if (errorMsg) setErrorMsg('');
              if (pageState === PageState.EnterPhone) {
                getActiveCoupons({
                  variables: {
                    phoneNumber,
                  },
                });
              }
            }}
          >
            <div
              style={{
                display: 'flex',
                width: '100%',
                flexDirection: 'column',
              }}
            >
              {(() => {
                if (pageState === PageState.EnterPhone) {
                  return (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        justifyContent: 'flex-start',
                        height: '400px',
                        padding: '40px',
                      }}
                    >
                      <HelpText>
                        Type in a customer's phone number below to redeem their
                        discount
                      </HelpText>

                      <PhoneNumberInput
                        type="tel"
                        value={phoneNumber}
                        placeholder="Customer Phone Number"
                        width="70%"
                        xswidth="100%"
                        onChange={(e) => {
                          setPhoneNumber(e.target.value);
                          if (errorMsg) setErrorMsg('');
                          if (pageState !== PageState.EnterPhone) setPageState(PageState.EnterPhone);
                        }}
                        required
                      />
                      <Button
                        margin="20px 0px"
                        text="CHECK FOR VALID DISCOUNT"
                        type="submit"
                        width="35%"
                        xswidth="70%"
                        loading={getCouponsLoading}
                      />
                    </div>
                  );
                }
                if (pageState === PageState.ValidCoupon) {
                  return (
                    <>
                      <ListHead>Available Coupons</ListHead>
                      <CouponList
                        coupons={activeCoupons}
                        setSelected={setSelectedCoupons}
                      />
                      <ButtonContainer>
                        <Button
                          margin="20px 0px 10px 0"
                          text="Don't Redeem"
                          type="button"
                          onClick={() => {
                            setPageState(PageState.EnterPhone);
                          }}
                          width="25%"
                          background={colors.grey4}
                          xswidth="40%"
                        />
                        <Button
                          margin="20px 0px 10px 0"
                          text="REDEEM DISCOUNT"
                          type="button"
                          onClick={() => redeemButtonClick()}
                          width="25%"
                          xswidth="40%"
                          loading={redeemLoading}
                        />
                      </ButtonContainer>
                    </>
                  );
                }
                if (pageState === PageState.CouponRedeemed) {
                  return (
                    <ValidCouponContainer>
                      <HeaderText>Success!</HeaderText>
                      <HelpText>
                        {`The discount was successfully redeemed and the user's
                          balance has been updated to $${Price.output(
                          balance,
                          true,
                        )}`}
                      </HelpText>
                      <Button
                        margin="25px 0px 0px"
                        text="GO BACK"
                        onClick={() => setPageState(PageState.EnterPhone)}
                        type="button"
                        width="100%"
                      />
                    </ValidCouponContainer>
                  );
                }
                if (pageState === PageState.InvalidCoupon) {
                  return (
                    <ValidCouponContainer>
                      <HeaderText>Discount Not Found</HeaderText>
                      <HelpText>
                        Any discount associated with this phone number has
                        either expired or does not exist
                      </HelpText>
                      <Button
                        margin="25px 0px 0px"
                        text="GO BACK"
                        onClick={() => setPageState(PageState.EnterPhone)}
                        type="button"
                        width="100%"
                        padding="0px"
                      />
                    </ValidCouponContainer>
                  );
                }
                return (
                  <Button
                    margin="20px 0px"
                    text="CHECK FOR VALID DISCOUNT"
                    type="submit"
                    width="fit-content"
                    padding="0px"
                    loading={getCouponsLoading}
                  />
                );
              })()}
              {errorMsg && <Error position="relative" error={errorMsg} />}
            </div>
          </Form>
        </Section>
      </Container>
    </>
  );
}
